import React, { useState } from 'react';
import { X, AlertTriangle, Zap, Crown, Clock, ExternalLink, CheckCircle, Copy, MessageCircle, CreditCard, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Plan configurations - matching the main PaymentModal
const RENEWAL_PLANS = {
  weekly: {
    id: 'weekly-plan',
    name: 'Weekly Plan',
    price: '$20',
    priceNum: 20,
    duration: '7 Days',
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    features: ['7 days full access', 'Signal generation', 'All features included'],
    squareLink: 'https://square.link/u/pcgnBKgL'  // Default Square link for weekly
  },
  monthly: {
    id: 'monthly-plan',
    name: 'Monthly Plan',
    price: '$59.99',
    priceNum: 59.99,
    duration: '30 Days',
    icon: Crown,
    color: 'from-purple-500 to-pink-500',
    features: ['30 days full access', 'Signal generation', 'Priority support', 'Best value - Save 25%'],
    recommended: true,
    squareLink: 'https://square.link/u/pcgnBKgL'  // Default Square link for monthly
  }
};

const WHATSAPP_LINK = "https://wa.me/message/3PBT5TZW3SARB1";
const QR_CODE_URL = "https://customer-assets.emergentagent.com/job_b4c8a9d0-7ea2-48c0-817a-eed35237bb3f/artifacts/0i16gwr9_qrccc.jpeg";
const WALLET_ADDRESS = "0x8c492198161A497276475B57880E1F2FD20e192D";

// API URL helper
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const RenewalModal = ({ isOpen, onClose, currentPlan, expiryDate, userEmail }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'card' or 'crypto'
  const [awaitingCardPayment, setAwaitingCardPayment] = useState(false);
  const [submittingRenewal, setSubmittingRenewal] = useState(false);

  if (!isOpen) return null;

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'Expired';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Expired';
    }
  };

  const handlePlanSelect = (planKey) => {
    setSelectedPlan(planKey);
  };

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(WALLET_ADDRESS);
    setCopiedAddress(true);
    toast.success('Wallet address copied!');
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handlePayWithCard = () => {
    if (!plan) return;
    
    // Open Square payment link
    toast.info('Redirecting to Square payment...');
    setAwaitingCardPayment(true);
    
    setTimeout(() => {
      window.open(plan.squareLink, '_blank');
    }, 500);
  };

  const handleCardPaymentComplete = async () => {
    if (!plan || !userEmail) return;
    
    setSubmittingRenewal(true);
    try {
      // Submit renewal request - goes to admin pending for review
      const response = await fetch(`${API}/payment/confirm-paylink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          plan: selectedPlan === 'weekly' ? 'essential' : 'premium'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Payment confirmation submitted! Your renewal is being processed.');
        setAwaitingCardPayment(false);
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toast.error(data.message || 'Failed to submit payment confirmation');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error('Failed to confirm payment. Please contact support.');
    } finally {
      setSubmittingRenewal(false);
    }
  };

  const plan = selectedPlan ? RENEWAL_PLANS[selectedPlan] : null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-red-900/30 to-orange-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Subscription Expired</h2>
                <p className="text-sm text-gray-400">Renew to continue using BonBot Signal Generator</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Plan Info */}
          <div className="mb-6 p-4 bg-red-500/10 rounded-xl border border-red-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Previous Plan</p>
                <p className="text-white font-semibold">{currentPlan || 'Standard Plan'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Status</p>
                <p className="text-red-400 font-bold">EXPIRED</p>
                <p className="text-xs text-gray-500">{formatExpiryDate(expiryDate)}</p>
              </div>
            </div>
          </div>

          {!selectedPlan ? (
            <>
              {/* Plan Selection */}
              <h3 className="text-lg font-semibold text-white mb-4">Select Your Renewal Plan</h3>
              
              <div className="space-y-4">
                {Object.entries(RENEWAL_PLANS).map(([key, planInfo]) => {
                  const Icon = planInfo.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handlePlanSelect(key)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left relative ${
                        planInfo.recommended 
                          ? 'border-purple-500 bg-purple-500/10 hover:bg-purple-500/20' 
                          : 'border-slate-600 hover:border-blue-500 bg-slate-800/30 hover:bg-slate-800/50'
                      }`}
                    >
                      {planInfo.recommended && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-xs font-bold text-white">
                          RECOMMENDED
                        </div>
                      )}
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${planInfo.color}`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-white font-semibold">{planInfo.name}</h4>
                            <div className="text-right">
                              <span className="text-2xl font-bold text-white">{planInfo.price}</span>
                              <span className="text-gray-400 text-sm ml-1">/ {planInfo.duration}</span>
                            </div>
                          </div>
                          <ul className="mt-2 space-y-1">
                            {planInfo.features.map((feature, idx) => (
                              <li key={idx} className="text-sm text-gray-400 flex items-center gap-2">
                                <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Payment Section - Direct to payment */}
              <div className="mb-4">
                <button 
                  onClick={() => {
                    setSelectedPlan(null);
                    setPaymentMethod(null);
                    setAwaitingCardPayment(false);
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                >
                  ← Change plan
                </button>
              </div>
              
              {/* Selected Plan Summary */}
              <div className="p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <plan.icon className="w-8 h-8 text-green-400" />
                    <div>
                      <h4 className="text-white font-bold">{plan.name}</h4>
                      <p className="text-gray-400 text-sm">{plan.duration} Access</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">{plan.price}</p>
                  </div>
                </div>
              </div>

              {/* Awaiting Card Payment Confirmation */}
              {awaitingCardPayment ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                    <h4 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Complete Your Square Payment
                    </h4>
                    <p className="text-gray-300 text-sm mb-3">
                      A new tab should have opened for Square payment. After completing your payment, click the button below.
                    </p>
                    <div className="space-y-3">
                      <Button
                        onClick={handleCardPaymentComplete}
                        disabled={submittingRenewal}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3"
                      >
                        {submittingRenewal ? 'Processing...' : "I've Completed Payment on Square"}
                      </Button>
                      <Button
                        onClick={handlePayWithCard}
                        variant="outline"
                        className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        Open Square Payment Again
                      </Button>
                      <button 
                        onClick={() => setAwaitingCardPayment(false)}
                        className="w-full text-gray-400 hover:text-white text-sm py-2"
                      >
                        Use different payment method
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-300 text-xs text-center">
                      ⏰ After confirmation, your renewal will be reviewed and activated within <strong>6 hours</strong>.
                    </p>
                  </div>
                </div>
              ) : !paymentMethod ? (
                <>
                  {/* Payment Method Selection */}
                  <h3 className="text-lg font-semibold text-white mb-4">Choose Payment Method</h3>
                  
                  <div className="space-y-3">
                    {/* Pay with Card Option */}
                    <button
                      onClick={() => {
                        setPaymentMethod('card');
                        handlePayWithCard();
                      }}
                      className="w-full p-4 rounded-xl border-2 border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 transition-all text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <CreditCard className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-bold">Pay with Card</p>
                            <p className="text-blue-400 text-xs">Secure payment via Square</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                            RECOMMENDED
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Pay with Crypto Option */}
                    <button
                      onClick={() => setPaymentMethod('crypto')}
                      className="w-full p-4 rounded-xl border-2 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
                            <Wallet className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-bold">Pay with Crypto</p>
                            <p className="text-orange-400 text-xs">USDT on BNB Smart Chain</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-300 text-xs">Up to 6h review</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              ) : paymentMethod === 'crypto' ? (
                <>
                  {/* Crypto Payment Details */}
                  <div className="mb-3">
                    <button 
                      onClick={() => setPaymentMethod(null)}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                    >
                      ← Change payment method
                    </button>
                  </div>
                  
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-orange-400" />
                      Pay with USDT (BEP-20)
                    </h4>
                    
                    {/* QR Code */}
                    <div className="flex justify-center mb-4">
                      <div className="p-2 bg-white rounded-lg">
                        <img 
                          src={QR_CODE_URL} 
                          alt="Payment QR Code" 
                          className="w-36 h-36 rounded"
                        />
                      </div>
                    </div>
                    
                    {/* Wallet Address */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-400 mb-1">Wallet Address (BNB Smart Chain)</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-slate-900 rounded text-xs text-gray-300 break-all font-mono">
                          {WALLET_ADDRESS}
                        </code>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={copyWalletAddress}
                          className="shrink-0 border-slate-600"
                        >
                          {copiedAddress ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                      <p className="text-amber-300 text-sm text-center font-medium">
                        Send exactly: <strong>{plan.price.replace('$', '')} USDT</strong>
                        <br />
                        <span className="text-xs">Network: BNB Smart Chain (BEP-20)</span>
                      </p>
                    </div>
                    
                    {/* WhatsApp Confirmation */}
                    <a 
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white text-center rounded-lg font-bold transition-colors"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Send Payment Screenshot via WhatsApp
                    </a>
                  </div>
                </>
              ) : null}
            </>
          )}

          {/* Activation Notice */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-300 font-medium">Important Note</p>
                <p className="text-gray-400 text-sm mt-1">
                  After payment confirmation, it may take up to <strong className="text-white">30 minutes</strong> for your access to be restored.
                </p>
              </div>
            </div>
          </div>

          {/* Support Link */}
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-sm">
              Need help? Contact{' '}
              <a 
                href="mailto:worood@forthewisepro.com" 
                className="text-blue-400 hover:underline"
              >
                support
              </a>
              {' '}or visit{' '}
              <a 
                href="https://forthewisepro.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                forthewisepro.com <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalModal;
